import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDialogFocus } from '../hooks/use-dialog-focus';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import {
  scanIdDocument,
  scanParcelLabel,
  IdScanSuccess,
  ParcelScanSuccess,
} from '../../api/id-scan-client';
import { FrameAnalyser, FrameQuality, PARCEL_THRESHOLDS } from './frame-quality';
import { decodeQr, decodeQrFromFile } from './qr-decode';
import {
  layout,
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
  zIndex,
  status,
} from '../../style-utils/theme';
import { ModalPortal } from '../modal/modal-portal';
import { Presence, m, overlayMotion, scrimMotion } from '../../motion';

// ---------------------------------------------------------------------------
// "Scan ID" — the capture half of autocapture.
// ---------------------------------------------------------------------------
//
// Lives inside PersonField, so EVERY name field in the product gets it from one
// place: the daily log, each visitor-log guest, the visitor's host, the parcel
// recipient and whoever collected the parcel.
//
// ============ IT CAPTURES ITSELF ============
// The camera opens and watches. When the frame is lit, still, full of document
// and in focus for several consecutive frames, it fires ONCE on its own. The
// concierge holds the passport over the counter and looks up; they do not aim,
// judge focus, and press a button while holding someone's document flat.
//
// The analysis is local and free (see frame-quality.ts), so watching costs
// nothing — only the shot that gets taken is billed. That asymmetry is what
// makes auto-capture affordable at all.
//
// ============ IT CAN ONLY FIRE ONCE ============
// `firedRef` is a one-shot latch checked and set in the same synchronous tick
// as the capture. A scan costs money and the loop runs several times a second;
// "it double-fired" would be a bill, not a glitch. The manual button routes
// through the same latch.
//
// Three further rules:
//   1. IT NEVER BLOCKS. Camera denied, budget spent, model off, image
//      unreadable — all end with the field still typeable and a sentence saying
//      so. A front desk cannot be stopped by a scanner.
//   2. IT NEVER OVERWRITES SILENTLY. What it fills is marked `Scanned`, and the
//      moment a human edits it that becomes `Scanned · edited` with the
//      original kept.
//   3. THE FRAME IS NOT KEPT. The image is passed to the request and dropped.

const pulse = keyframes`
  0%   { opacity: 0.55; }
  50%  { opacity: 1; }
  100% { opacity: 0.55; }
`;

const Wrap = styled.div`
  display: inline-flex;
`;

// THE PROMPT, not a pill. A concierge who does not know scanning exists will
// type every name by hand forever, so this reads as an offer rather than an
// affordance to be discovered: brand fill, full label, camera glyph, and enough
// height to be an obvious tap target on a counter tablet.
const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  /* The comment above says "enough height to be an obvious tap target on a
     counter tablet" and the number said 40px, which is below the 44px minimum
     the design system sets for exactly that surface. Now it is the token, so it
     cannot drift from the rule again. */
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.md};
  border: none;
  border-radius: ${radius.sm};
  background: ${palette.lagoon};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  cursor: pointer;
  box-shadow: ${shadow.brand};
  transition: ${transition.base};

  &:hover:not(:disabled) {
    background: ${palette.irisDark};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }

  /* KEYBOARD GETS WHAT THE POINTER GETS — this styled :hover and nothing for
     focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    background: ${palette.surfaceMuted};
    color: ${palette.slateLight};
    box-shadow: none;
    cursor: default;
  }
`;

const CameraGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 8h3l1.5-2h7L17 8h3a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

/* The camera sheet rises from the bottom edge over a fading scrim, and leaves
   the same way - the conditional below sits inside a Presence. */
const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};
`;

const Sheet = m.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  width: 100%;
  max-width: 620px;
  padding: ${spacing.md};
  background: ${palette.surface};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Stage = styled.div`
  position: relative;
  border-radius: ${radius.sm};
  overflow: hidden;
  background: ${palette.ink};
`;

const Video = styled.video`
  display: block;
  width: 100%;
  max-height: 46vh;
  max-height: 46dvh;
  object-fit: cover;
`;

/* The target the document should fill. ID-1 cards and passport data pages are
   both close enough to 1.58:1 that one guide serves for both. */
const Guide = styled.div<{ ready: boolean }>`
  position: absolute;
  inset: 8% 6%;
  border: 2px solid ${p => (p.ready ? palette.success : 'rgba(255,255,255,0.7)')};
  border-radius: ${radius.xs};
  box-shadow: 0 0 0 9999px rgba(18, 41, 44, 0.35);
  transition: ${transition.colors};
  pointer-events: none;
`;

const Status = styled.div<{ ready: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${spacing.xs} ${spacing.sm};
  background: ${p => (p.ready ? palette.success : 'rgba(18,41,44,0.72)')};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  text-align: center;
  animation: ${p => (p.ready ? 'none' : `${pulse} 1.8s ease-in-out infinite`)};
`;

const Hint = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  justify-content: space-between;
  align-items: center;
`;

/* The photo fallback's input.
   NOT display:none and NOT hidden: Safari has a long history of refusing to
   open the camera for a file input it considers unrendered, and this control
   exists precisely for the browsers that behave that way. It stays in the
   layout, clipped to a pixel and out of the tab order. */
const PhotoInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

const Action = styled.button<{ primary?: boolean }>`
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${p => (p.primary ? palette.iris : palette.lineStrong)};
  border-radius: ${radius.sm};
  background: ${p => (p.primary ? palette.iris : 'transparent')};
  color: ${p => (p.primary ? palette.white : palette.slate)};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: ${transition.colors};

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Captured = styled.ol`
  margin: 0;
  padding-left: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

const Alert = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${status.alert.fg};
`;

export interface ScanIdButtonProps {
  buildingId?: number | null;
  /**
   * Called once per captured document. `index` is 0-based, so a sequence of
   * three guests calls this with 0, 1, 2.
   */
  onResult?: (r: IdScanSuccess & ParcelScanSuccess, index: number) => void;
  /**
   * mode="pass" only: the text of the QR code the camera read (the guest's
   * pass page URL). Called once; the sheet then closes.
   */
  onPass?: (decoded: string) => void;
  disabled?: boolean;
  label?: string;
  /**
   * How many documents to take in one sitting. 1 (the default) closes the sheet
   * after the first. Above 1 the sheet STAYS OPEN and walks the queue — the
   * concierge scans four passports for a family without touching the screen
   * between them.
   *
   * Also the hard ceiling on how many paid requests one opening of this sheet
   * can make.
   */
  sequenceTotal?: number;
  /**
   * What is being photographed.
   *
   * 'id'     an identity document -> /api/id-scan, returns a name.
   * 'parcel' a shipping label      -> /api/parcel-scan, returns a name PLUS
   *          the unit, the carrier and the tracking number.
   *
   * It also switches the frame-quality thresholds, which is not cosmetic: a
   * label sits on a BROWN BOX, and the "is this paper" test tuned for a pale
   * ID card rejects cardboard forever.
   *
   * 'pass'   the QR on a guest's phone (resident-portal Plan 6) -> decoded
   *          ON THIS DEVICE (BarcodeDetector, else jsQR — qr-decode.ts) and
   *          handed to onPass. NO server call, nothing uploaded, nothing
   *          billed; the frame-quality analyser is not used.
   */
  mode?: 'id' | 'parcel' | 'pass';
}

// How often the pass mode looks for a QR code in the live picture. A decode of
// an 800px frame takes a few tens of milliseconds even in jsQR.
const PASS_DECODE_INTERVAL_MS = 250;

// ---------------------------------------------------------------------------
// THE PHOTO A LIVE STREAM CANNOT TAKE.
// ---------------------------------------------------------------------------
//
// getUserMedia is not available everywhere a front desk actually works:
//
//   * an in-app browser (a link opened inside Instagram, Facebook, Gmail or
//     Teams on iOS) runs in a WKWebView that is not granted camera access;
//   * a page served over plain http — a LAN IP on the desk's own network — is
//     not a secure context, and `navigator.mediaDevices` is then UNDEFINED;
//   * Safari REMEMBERS a denial. Once someone has pressed "Don't Allow", the
//     prompt never reappears and every later call rejects instantly;
//   * iOS Low Power Mode refuses inline video playback, so the stream opens and
//     the <video> never plays.
//
// A file input with `capture` hands the job to the operating system's own
// camera app instead, which works in every one of those cases. It is the
// fallback, never the default: it takes a photo per tap and cannot auto-capture.
const MAX_EDGE = 1600;

const canvasToJpeg = (
  source: CanvasImageSource,
  width: number,
  height: number
): string => {
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

  // Downscaled to the same 1600px long edge the live path uses: well above what
  // the model needs to read a document, and it keeps the upload — and the image
  // token count, which is what the scan actually costs — down. A phone photo
  // straight off the sensor is 4000px and several megabytes.
  return canvas.toDataURL('image/jpeg', 0.85);
};

// A photo file -> the same data URL the live path produces.
//
// createImageBitmap is the direct route and Safari has had it since 15. The
// <img> fallback exists for the iPhones still running 14 that are genuinely
// sitting on front desks, and it is also what handles a decode the bitmap path
// refuses.
const photoToDataUrl = async (file: File): Promise<string> => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      const out = canvasToJpeg(bitmap, bitmap.width, bitmap.height);
      bitmap.close?.();

      return out;
    } catch {
      // fall through to the <img> path
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode failed'));
      el.src = url;
    });

    return canvasToJpeg(image, image.naturalWidth, image.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
};

// WHY THE CAMERA DID NOT OPEN, in words a front desk can act on.
//
// One sentence for all of it — "No camera available" — was true and useless:
// the fix for a denied permission (change it in Settings) has nothing in common
// with the fix for an in-app browser (open it in Safari), and someone told the
// generic sentence tries neither.
// `instead` is the way forward without a camera: typing the name for a
// document, searching today's list for a pass.
const cameraFailureMessage = (error: unknown, instead = 'type the name'): string => {
  const name =
    typeof error === 'object' && error !== null && 'name' in error
      ? String((error as { name: unknown }).name)
      : '';

  if (!window.isSecureContext) {
    return `The camera needs a secure (https) connection. Open this site over https, or ${instead} instead.`;
  }
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      // Safari never asks twice. Saying "allow it and try again" to somebody who
      // will never see the prompt again is what makes this look broken.
      return `Camera access is blocked for this site. Allow it in your browser settings (on iPhone: Settings › Apps › Safari › Camera), take a photo instead, or ${instead}.`;
    case 'NotFoundError':
    case 'OverconstrainedError':
      return `No camera was found on this device. Take a photo instead, or ${instead}.`;
    case 'NotReadableError':
    case 'AbortError':
      return `The camera is being used by another app or tab. Close it and try again, take a photo instead, or ${instead}.`;
    default:
      return `The camera could not be opened here. Take a photo instead, or ${instead}.`;
  }
};

// How many consecutive good frames before firing. At ~8 analyses a second this
// is roughly half a second of the document being genuinely still and sharp —
// long enough that a frame which happens to look good mid-movement cannot
// trigger a paid request, short enough not to feel like waiting.
const GOOD_FRAMES_REQUIRED = 6;
const ANALYSE_INTERVAL_MS = 120;
// The camera needs a moment to expose and focus after opening, and the user
// needs a moment to aim. Auto-capture stays disarmed until then.
const ARMING_DELAY_MS = 1400;

export const ScanIdButton: React.FC<ScanIdButtonProps> = ({
  buildingId,
  onResult,
  onPass,
  disabled,
  label = 'Scan ID',
  sequenceTotal = 1,
  mode = 'id',
}) => {
  const passMode = mode === 'pass';
  // A pass is one code, read once: there is no sequence.
  const total = passMode ? 1 : Math.max(1, sequenceTotal);
  const instead = passMode ? 'search the list' : 'type the name';

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quality, setQuality] = useState<FrameQuality | null>(null);
  const [armed, setArmed] = useState(false);
  const [autoOn, setAutoOn] = useState(true);
  // Names captured so far this sitting, for the running list in the sheet.
  const [captured, setCaptured] = useState<string[]>([]);
  // The stream, in STATE as well as in a ref. The ref is what stop() releases;
  // this is what an effect binds to the <video>, and it has to be state because
  // the sheet — and therefore the <video> — does not exist yet at the moment
  // getUserMedia resolves. Assigning srcObject inline raced that render, and on
  // the render it lost the stream was simply never attached: a black rectangle,
  // videoWidth stuck at 0, and a Capture button that did nothing at all.
  const [stream, setStream] = useState<MediaStream | null>(null);
  // Has the <video> actually produced a frame? NOT the same as "the stream
  // opened". iOS Low Power Mode refuses inline playback outright, and Safari can
  // hand back a stream whose element never leaves the paused state.
  const [videoReady, setVideoReady] = useState(false);
  // The live camera is unavailable — in-app browser, denied permission, no
  // secure context. The sheet stays open on the photo fallback instead of
  // showing a dead viewfinder.
  const [cameraDown, setCameraDown] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<FrameAnalyser | null>(null);
  const loopRef = useRef<number | null>(null);
  const goodRef = useRef(0);
  // THE LATCH. Held while a request is in flight and released only by the
  // sequence logic below, so no two triggers can ever produce two requests.
  const firedRef = useRef(false);
  // How many documents this sitting has already paid for. Bounded by `total`.
  const doneRef = useRef(0);

  // ============ THE SAME PASSPORT MUST NOT BE SCANNED TWICE ============
  // After a capture the document is usually still lying on the counter, still
  // sharp and still still — so the very next frame would satisfy every quality
  // test and fire again. And again.
  //
  // So between guests the analyser must OBSERVE A NOT-READY FRAME before
  // auto-capture re-arms: the concierge has to lift the document away and put
  // the next one down. That physical gesture is the signal, and it is the only
  // one that reliably distinguishes "the next guest" from "the same guest, one
  // eighth of a second later".
  const awaitingClearRef = useRef(false);
  // Mirrored into state purely so the status line can say so — a ref change
  // does not re-render, and "take that one away" is the instruction the
  // concierge is waiting for.
  const [awaitingClear, setAwaitingClear] = useState(false);

  const stop = useCallback(() => {
    if (loopRef.current !== null) {
      window.clearInterval(loopRef.current);
      loopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setStream(null);
    setVideoReady(false);
  }, []);

  // Release the camera on unmount as well as on close. A wizard step navigated
  // away from with the sheet open would otherwise leave the device light on,
  // which users read — correctly — as still being recorded.
  useEffect(() => stop, [stop]);

  const close = useCallback(() => {
    stop();
    setOpen(false);
    setBusy(false);
    setQuality(null);
    setArmed(false);
    setCameraDown(false);
    goodRef.current = 0;
    awaitingClearRef.current = false;
    setAwaitingClear(false);
  }, [stop]);

  // ============ BIND THE STREAM TO THE ELEMENT, AND KEEP TRYING TO PLAY ======
  //
  // Both halves of this were one inline line before, and both failed on iOS.
  //
  //   THE ELEMENT MAY NOT EXIST YET. The sheet renders in a portal after
  //   setOpen(true); getUserMedia can resolve first (it does whenever the
  //   permission is already granted). An effect that runs when BOTH the stream
  //   and the element exist cannot lose that race.
  //
  //   play() CAN REJECT, AND THE REJECTION WAS SWALLOWED. Low Power Mode alone
  //   is enough. A paused <video> reports videoWidth 0 forever, which made the
  //   analyser return null, left the status on "Starting the camera…" and made
  //   Capture a no-op — the exact shape of "the scanner does not work on my
  //   phone". Retrying on loadedmetadata/canplay covers the ordinary case, and
  //   the failure is now SAID rather than hidden.
  useEffect(() => {
    const video = videoRef.current;
    if (!open || !stream || !video) return;

    video.srcObject = stream;

    let cancelled = false;
    const tryPlay = () => {
      video.play().then(
        () => {
          if (!cancelled && video.videoWidth) setVideoReady(true);
        },
        () => {
          if (!cancelled) {
            setError(
              'This device would not start the live camera. Take a photo instead, or type the name.'
            );
          }
        }
      );
    };

    const onLoaded = () => {
      if (!cancelled && video.videoWidth) setVideoReady(true);
      tryPlay();
    };

    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('canplay', onLoaded);
    tryPlay();

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('canplay', onLoaded);
    };
  }, [open, stream]);

  // The analysis loop, restartable — `start` opens it and each sequence step
  // resumes it after a capture.
  const startLoop = useCallback(() => {
    if (loopRef.current !== null) window.clearInterval(loopRef.current);

    loopRef.current = window.setInterval(() => {
      const v = videoRef.current;
      const a = analyserRef.current;
      if (!v || !a || firedRef.current) return;

      const q = a.analyse(v);
      if (!q) return;
      setQuality(q);

      // THE CLEAR GATE. Between guests, hold everything until a NOT-READY
      // frame has been seen — the previous document lifted off the counter.
      // Without it the passport just scanned is still sharp and still still,
      // and the sequence would burn through its whole allowance on one person.
      if (awaitingClearRef.current) {
        if (!q.ready) {
          awaitingClearRef.current = false;
          setAwaitingClear(false);
        }
        goodRef.current = 0;

        return;
      }

      if (q.ready) goodRef.current += 1;
      // A single bad frame resets the run. Requiring CONSECUTIVE good frames
      // is what separates "the document is being held still" from "a blurred
      // frame happened to score well while the hand was moving".
      else goodRef.current = 0;
    }, ANALYSE_INTERVAL_MS);
  }, []);

  // ============ ONE SCAN, WHEREVER THE PICTURE CAME FROM ============
  //
  // The live viewfinder and the operating system's camera app produce the same
  // thing: a downscaled JPEG data URL. Everything after that — the one-shot
  // latch, the refusal handling, the sequence, the running list — is identical,
  // and a second copy of it for the fallback path would be a second place for
  // the double-charge rule to be got wrong.
  const runScan = useCallback(
    async (image: string) => {
      setBusy(true);
      setError(null);

      try {
        const result =
          mode === 'parcel'
            ? await scanParcelLabel({ image, buildingId: Number(buildingId) })
            : await scanIdDocument({ image, buildingId });

        if (!result.ok) {
          // A refusal is the scanner saying "use the keyboard", not an error.
          // The latch is released so they can retake — but auto-capture is NOT
          // re-armed after a failure, because a document the model could not
          // read will very likely fail again, and re-arming turns one bad
          // photograph into a loop of paid requests.
          setError(result.message);
          setAutoOn(false);
          setBusy(false);
          firedRef.current = false;
          setQuality(null);

          return;
        }

        const index = doneRef.current;
        doneRef.current += 1;

        // The consumer's handler is OUTSIDE this component's control and it
        // writes into the parent's form state. If it throws — a shape it did
        // not expect, a stale array — the throw would propagate out of this
        // async function, leave firedRef latched and the analysis loop stopped,
        // and the sheet would sit there green and dead. Which is exactly the
        // failure this catch exists to prevent: the scan SUCCEEDED and was
        // paid for, so the last thing to do is discard it because the caller
        // stumbled.
        try {
          onResult?.(result as IdScanSuccess & ParcelScanSuccess, index);
        } catch (e) {
          console.error('[ScanId] onResult handler threw:', e);
          setError(
            'The document was read, but the form could not accept it. Type the name instead.'
          );
          setAutoOn(false);
          setBusy(false);
          firedRef.current = false;

          return;
        }
        setCaptured(prev => [
          ...prev,
          mode === 'parcel'
            ? [
                (result as ParcelScanSuccess).recipient_name,
                (result as ParcelScanSuccess).unit_label &&
                  `— ${(result as ParcelScanSuccess).unit_label}`,
              ]
                .filter(Boolean)
                .join(' ') || '(nothing read)'
            : `${(result as IdScanSuccess).first_name} ${(result as IdScanSuccess).last_name}`.trim() ||
              '(no name read)',
        ]);

        // Last one — or a single-shot button. Close and hand back.
        if (doneRef.current >= total) {
          close();

          return;
        }

        // MORE TO GO. Keep the sheet and the camera open, and restart the
        // analysis loop for the next document — but do NOT re-arm yet: the
        // passport just scanned is still on the counter and would fire again
        // immediately. awaitingClear makes the concierge lift it away first.
        setBusy(false);
        setQuality(null);
        goodRef.current = 0;
        firedRef.current = false;
        analyserRef.current?.reset();
        // Only the LIVE path needs the clear gate and the analysis loop. On the
        // photo fallback there is no loop to restart and nothing that could fire
        // on its own, so demanding the document be lifted away would just be an
        // instruction with no mechanism behind it.
        if (streamRef.current) {
          awaitingClearRef.current = true;
          setAwaitingClear(true);
          startLoop();
        }
      } catch {
        setError('That did not go through. Try again, or type the name instead.');
        setAutoOn(false);
        setBusy(false);
        firedRef.current = false;
      }
    },
    [buildingId, onResult, close, total, startLoop, mode]
  );

  // ============ A PASS: READ THE CODE HERE, HAND IT BACK, CLOSE ============
  // mode="pass" (resident-portal Plan 6). The same latch as a paid scan — one
  // code, one hand-back — but nothing leaves the device and nothing is billed,
  // so the loop simply keeps looking until it sees a code.
  const decodingRef = useRef(false);
  const finishPass = useCallback(
    (decoded: string) => {
      try {
        onPass?.(decoded);
      } catch (e) {
        console.error('[ScanId] onPass handler threw:', e);
      }
      close();
    },
    [onPass, close]
  );

  const startPassLoop = useCallback(() => {
    if (loopRef.current !== null) window.clearInterval(loopRef.current);

    loopRef.current = window.setInterval(() => {
      const v = videoRef.current;
      if (!v || !v.videoWidth || firedRef.current || decodingRef.current) return;
      decodingRef.current = true;
      decodeQr(v, v.videoWidth, v.videoHeight)
        .then(decoded => {
          if (!decoded || firedRef.current) return;
          firedRef.current = true;
          finishPass(decoded);
        })
        .finally(() => {
          decodingRef.current = false;
        });
    }, PASS_DECODE_INTERVAL_MS);
  }, [finishPass]);

  // "Capture now" in pass mode: read the frame that is on screen, once.
  const capturePass = useCallback(async () => {
    if (firedRef.current) return;
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setError('The camera has not produced a picture on this device. Take a photo instead, or search the list.');

      return;
    }
    setBusy(true);
    const decoded = await decodeQr(video, video.videoWidth, video.videoHeight);
    setBusy(false);
    if (!decoded) {
      setError('No pass code in view. Ask the guest to hold their phone closer, with the code on the screen.');

      return;
    }
    if (firedRef.current) return;
    firedRef.current = true;
    finishPass(decoded);
  }, [finishPass]);

  // The LIVE path: grab the current frame and scan it.
  const capture = useCallback(async () => {
    if (passMode) {
      await capturePass();

      return;
    }
    // Checked and set in one synchronous step, before any await, so two
    // triggers in the same tick cannot both pass.
    if (firedRef.current) return;
    const video = videoRef.current;

    // NOT A SILENT RETURN. This is the state an iPhone that refused to play the
    // stream sits in permanently, and returning quietly made "Capture now" a
    // button that visibly did nothing — the single most reported symptom.
    if (!video || !video.videoWidth) {
      setError(
        'The camera has not produced a picture on this device. Take a photo instead, or type the name.'
      );
      setAutoOn(false);

      return;
    }
    firedRef.current = true;

    // Stop analysing the moment we commit. Nothing good comes of the loop
    // continuing to evaluate frames while a request is in flight.
    if (loopRef.current !== null) {
      window.clearInterval(loopRef.current);
      loopRef.current = null;
    }

    let image: string;
    try {
      image = canvasToJpeg(video, video.videoWidth, video.videoHeight);
    } catch {
      setError('That frame could not be read. Try again, or type the name instead.');
      firedRef.current = false;

      return;
    }

    await runScan(image);
  }, [runScan, passMode, capturePass]);

  // The FALLBACK path: a photo taken by the operating system's camera app.
  const onPickPhoto = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files && event.target.files[0];
      // Reset the input immediately: without this, photographing the SAME
      // document twice in a row fires no change event the second time and the
      // desk is left pressing a button that does nothing.
      event.target.value = '';
      if (!file) return;
      if (firedRef.current) return;
      firedRef.current = true;

      setBusy(true);
      setError(null);
      // A pass: read the photo's QR here, never upload it.
      if (passMode) {
        const decoded = await decodeQrFromFile(file);
        setBusy(false);
        if (!decoded) {
          firedRef.current = false;
          setError('No pass code found in that photo. Try again with the code filling more of the picture, or search the list.');

          return;
        }
        finishPass(decoded);

        return;
      }
      let image: string;
      try {
        image = await photoToDataUrl(file);
      } catch {
        setError('That photo could not be read. Try again, or type the name instead.');
        setBusy(false);
        firedRef.current = false;

        return;
      }

      await runScan(image);
    },
    [runScan, passMode, finishPass]
  );

  const start = async () => {
    setError(null);
    setQuality(null);
    setArmed(false);
    setAutoOn(true);
    setCameraDown(false);
    setVideoReady(false);
    goodRef.current = 0;
    firedRef.current = false;
    doneRef.current = 0;
    awaitingClearRef.current = false;
    setAwaitingClear(false);
    setCaptured([]);
    setOpen(true);

    // NOT AN EXCEPTION TO CATCH — an absence to detect. Over plain http, and in
    // some in-app webviews, `navigator.mediaDevices` is undefined outright, so
    // reaching for .getUserMedia on it throws a TypeError whose message says
    // nothing a front desk can act on. The sheet opens on the photo fallback.
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraDown(true);
      setError(
        window.isSecureContext
          ? `This browser will not give the page a camera. Take a photo instead, or ${instead}.`
          : `The camera needs a secure (https) connection. Take a photo instead, or ${instead}.`
      );

      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // The rear camera on a phone or tablet — what a desk uses to photograph
        // a document lying on the counter.
        // focusMode is cast in because it is NOT in the TypeScript DOM lib —
        // it is a real, widely-shipped constraint (Chrome/Android especially)
        // that lib.dom.d.ts has never picked up. Browsers that do not know it
        // ignore it; browsers that do give continuous autofocus, without which
        // a fixed-focus stream can sit just off sharp forever and auto-capture
        // waits for a threshold it will never reach.
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          focusMode: 'continuous',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        audio: false,
      });
      streamRef.current = stream;
      // Handed to the effect above rather than assigned here: the <video> may
      // not be mounted yet, and it was that race which left the element without
      // a stream on the devices where the permission was already granted.
      setStream(stream);

      // A pass needs no quality gate and no arming delay: it is not billed,
      // and a code is either readable or not.
      if (passMode) {
        setArmed(true);
        startPassLoop();

        return;
      }

      analyserRef.current = new FrameAnalyser(
        mode === 'parcel' ? PARCEL_THRESHOLDS : undefined
      );
      analyserRef.current.reset();
      window.setTimeout(() => setArmed(true), ARMING_DELAY_MS);

      startLoop();
    } catch (e) {
      // The stream never opened. Say WHICH of the several reasons it was, and
      // keep the sheet open on the photo fallback — a desk that cannot use the
      // live camera can still scan, which is the difference between a degraded
      // feature and a broken one.
      setCameraDown(true);
      setError(cameraFailureMessage(e, instead));
      // The name is the diagnosis and it never reaches the user: "it does not
      // work on my phone" and "NotAllowedError on a device whose permission was
      // denied three weeks ago" are the same report, and only one of them can be
      // acted on. No PII — a DOMException name and message, nothing about the
      // document or the person.
      console.error(
        '[ScanId] camera failed:',
        (e as { name?: string })?.name,
        (e as { message?: string })?.message,
        'secureContext=' + String(window.isSecureContext)
      );
    }
  };

  // Firing lives OUTSIDE the interval so it reads current state (armed, autoOn)
  // rather than the values captured when the loop was created.
  useEffect(() => {
    if (passMode) return;
    if (!open || !armed || !autoOn || busy || firedRef.current) return;
    if (!quality || !quality.ready) return;
    if (goodRef.current < GOOD_FRAMES_REQUIRED) return;
    capture();
  }, [quality, open, armed, autoOn, busy, capture, passMode]);

  const stepLabel = total > 1 ? `Guest ${Math.min(captured.length + 1, total)} of ${total} — ` : '';
  const statusText = passMode
    ? busy
      ? 'Reading the code…'
      : armed && !videoReady
      ? 'The live camera did not start — use Take a photo'
      : !armed
      ? 'Starting the camera…'
      : 'Point the camera at the pass code on the guest’s phone'
    : busy
    ? 'Reading the document…'
    : awaitingClear
    ? `${stepLabel}take that one away, then place the next`
    : // "Starting" is only honest while it might still start. Once the element
      // has been given a stream and has still produced no frame after the arming
      // delay, the live camera is not coming — say so and point at the photo
      // button rather than pulsing "Starting the camera…" forever, which is what
      // an iPhone in Low Power Mode used to do.
      armed && !videoReady
    ? 'The live camera did not start — use Take a photo'
    : !armed
    ? 'Starting the camera…'
    : !autoOn
    ? 'Auto-capture off — press Capture when ready'
    : quality?.ready
    ? 'Hold it there…'
    : stepLabel +
      (quality?.hint ||
        (mode === 'parcel'
          ? 'Point the camera at the label'
          : 'Point the camera at the document'));

  /* The sheet's accessible name, and the visible title, from one expression —
     so the two can never describe different things. */
  const scanDialogLabel =
    mode === 'parcel'
      ? 'Scan a parcel label'
      : passMode
      ? 'Scan a pass'
      : total > 1
        ? `Scan ${total} IDs`
        : 'Scan an ID';

  /* Focus trap, initial focus, restore-on-close and Escape, from the shared
     dialog stack — so a scan opened from inside a modal takes Escape without
     also closing the form behind it. */
  const scanDialogRef = useDialogFocus<HTMLDivElement>(open, close);

  return (
    <Wrap>
      <Button type="button" onClick={start} disabled={disabled}>
        <CameraGlyph />
        {label}
      </Button>

      <Presence>
        {open ? (
        <ModalPortal key="scan-sheet">
          <Scrim
            {...scrimMotion}
            onMouseDown={e => {
              if (e.target === e.currentTarget) close();
            }}
          >
            {/* A REAL DIALOG.
                This overlay covers the whole viewport, takes over the camera
                and is the only way to dismiss what it shows — and it carried no
                role, no aria-modal, no accessible name, no focus trap and no
                Escape. Focus stayed on the "Scan ID" button behind the scrim,
                so Tab walked the page underneath a live camera sheet, and
                assistive tech was never told anything had opened.
                `useDialogFocus` supplies the trap, the initial focus, the
                restore on close and the shared Escape ordering — the same
                contract every other modal in the product already uses. */}
            <Sheet
              {...overlayMotion('sheet')}
              ref={scanDialogRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label={scanDialogLabel}
            >
              <Title>{scanDialogLabel}</Title>
              <Hint>
                {mode === 'parcel'
                  ? 'Point the camera at the shipping label. It captures on its own once the label is sharp and still.'
                  : passMode
                  ? 'Ask the guest to show the pass code on their phone. It is read on this tablet, on its own — nothing is sent or kept.'
                  : 'Any passport, national ID, driving licence or residence permit. Lay it flat inside the frame — it captures on its own once it is sharp and still.'}
                {total > 1 &&
                  ' Take each one away before placing the next.'}
              </Hint>

              {/* The viewfinder is dropped entirely when the live camera never
                  opened. A black rectangle over a permanent "Starting the
                  camera…" reads as a broken screen; the photo button reads as a
                  way to get the job done. */}
              {!cameraDown && (
                <Stage>
                  {/* autoPlay as well as the explicit play() in the effect: on
                      iOS one of the two usually succeeds where the other does
                      not, and a paused element reports videoWidth 0, which
                      silently disables both auto-capture and Capture now. */}
                  <Video ref={videoRef} playsInline muted autoPlay />
                  <Guide ready={!passMode && !!quality?.ready && armed && autoOn} />
                  <Status ready={!passMode && !!quality?.ready && armed && autoOn}>{statusText}</Status>
                </Stage>
              )}

              {/* The fallback, and NOT hidden behind the failure: a desk whose
                  live camera is merely bad (a fixed-focus webcam that never
                  reaches the sharpness threshold) needs it just as much as one
                  whose camera never opened.

                  `capture="environment"` asks the operating system for the rear
                  camera. iOS honours it from Safari, from every third-party
                  browser and from inside an in-app webview — which is exactly
                  the set of places getUserMedia does not work. */}
              <PhotoInput
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPickPhoto}
                aria-hidden="true"
                tabIndex={-1}
              />

              {captured.length > 0 && (
                <Captured>
                  {captured.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </Captured>
              )}

              {error && <Alert>{error}</Alert>}

              <Row>
                <Action type="button" onClick={close}>
                  {captured.length > 0 ? 'Done' : 'Cancel'}
                </Action>
                {/* Take a photo — the OS camera. PRIMARY whenever the live
                    stream is not delivering frames, because then it is the only
                    thing on this sheet that can complete the job. */}
                <Action
                  type="button"
                  primary={cameraDown || !videoReady}
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                >
                  Take a photo
                </Action>
                {/* Manual capture always stays. Auto-capture depends on focus,
                    lighting and a camera that reports them honestly; on the
                    hardware where it does not, the desk still has to be able to
                    take the photograph. Hidden only when there is no live
                    camera at all — a button that cannot work is worse than an
                    absent one. */}
                {!cameraDown && (
                  <Action
                    type="button"
                    primary={videoReady}
                    onClick={() => capture()}
                    disabled={busy}
                  >
                    {busy ? 'Reading…' : 'Capture now'}
                  </Action>
                )}
              </Row>
            </Sheet>
          </Scrim>
        </ModalPortal>
        ) : null}
      </Presence>
    </Wrap>
  );
};
